import React, { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Button from '@material-ui/core/Button'
import MenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import Popover from '@material-ui/core/Popover'
import ListItemText from '@material-ui/core/ListItemText'
import { makeStyles } from '@material-ui/core/styles'
import KeyboardArrowDownIcon from 'components/material-symbol/icons/KeyboardArrowDown'
import {
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_BORDER,
  OUTLINED_INPUT_BORDER_HOVER,
  OUTLINED_INPUT_BORDER_RADIUS,
  OUTLINED_INPUT_FOCUS_BORDER,
} from 'lib/form-field-styles'

const useStyles = makeStyles(theme => ({
  trigger: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textAlign: 'left',
    height: 40,
    minHeight: 40,
    padding: theme.spacing(1, 1.5),
    borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
    border: `1px solid ${OUTLINED_INPUT_BORDER}`,
    backgroundColor: theme.palette.common.white,
    color: CALLOUT_FOREGROUND,
    fontSize: '0.875rem',
    fontWeight: 500,
    lineHeight: 1.25,
    textTransform: 'none',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F4F6F8',
      borderColor: OUTLINED_INPUT_BORDER_HOVER,
      boxShadow: 'none',
    },
    '&$triggerOpen': {
      borderColor: OUTLINED_INPUT_FOCUS_BORDER,
      backgroundColor: theme.palette.common.white,
    },
  },
  triggerOpen: {},
  triggerLabel: {
    flex: '1 1 auto',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  triggerIcon: {
    color: CALLOUT_FOREGROUND,
    marginLeft: theme.spacing(1),
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  menuPaper: {
    marginTop: theme.spacing(0.5),
    borderRadius: 12,
    boxShadow: theme.shadows[8],
  },
}))

const SettingsSelect = ({ value, options, onChange, label, className }) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const selectedOption = options.find(option => option.value === value)

  const handleOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleSelect = nextValue => () => {
    onChange(nextValue)
    handleClose()
  }

  return (
    <>
      <Button
        type="button"
        className={clsx(
          classes.trigger,
          { [classes.triggerOpen]: open },
          className,
        )}
        onClick={handleOpen}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        <span className={classes.triggerLabel}>
          {selectedOption?.label ?? ''}
        </span>
        <span className={classes.triggerIcon}>
          <KeyboardArrowDownIcon fontSize="small" />
        </span>
      </Button>
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          className: classes.menuPaper,
          style: anchorEl ? { minWidth: anchorEl.clientWidth } : undefined,
        }}
      >
        <MenuList autoFocusItem role="listbox">
          {options.map(option => (
            <MenuItem
              key={option.value}
              selected={option.value === value}
              aria-selected={option.value === value}
              onClick={handleSelect(option.value)}
            >
              <ListItemText primary={option.label} />
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  )
}

SettingsSelect.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    }),
  ).isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
}

export default SettingsSelect
